import type { ModelValidator } from 'framework/mobx-integration';
import type { AntSelectOption } from 'controls/react/ant/ant-select/types';
import { OAUTH2_RFC6749_TYPE } from 'areas/administration/authentication/oauth2/types';
import { SMTP_TYPE } from 'areas/administration/authentication/smtp/types';
import type { AuthenticationFormStore } from 'areas/administration/authentication/components/authentication-form/authenticationFormStore';
import type { SimpleFieldsStore } from 'areas/administration/authentication/components/simple-fields/simpleFieldsStore';
import type { OAuthValidationStore } from 'areas/administration/authentication/components/oauth-validation/oauthValidationStore';

export const AuthenticationType = {
	Wmi: 'WMI',
	Generic: 'GENERIC',
	Ssh: 'SSH',
	Jdbc: 'JDBC',
	SnmpV1: 'SNMPv1',
	SnmpV2c: 'SNMPv2c',
	SnmpV3: 'SNMPv3',
	Amazon: 'AMAZON',
	Azure: 'AZURE',
	GoogleCloud: 'GOOGLE_CLOUD',
	OAuth2: 'OAUTH2',
	OAuth2Rfc6749: OAUTH2_RFC6749_TYPE,
	Smtp: SMTP_TYPE,
} as const;

export type AuthenticationType = (typeof AuthenticationType)[keyof typeof AuthenticationType];

export type AuthenticationFormMode = 'create' | 'update' | 'view';

export type AuthenticationFormModel = {
	name: string;
	description: string;
	tags: string[];
	authenticationType: AuthenticationType;
};

export type AuthenticationRecord = AuthenticationFormModel & {
	id: string;
	authenticationTypeText?: string;
	[field: string]: unknown;
};

export type AuthenticationFormConfig = {
	id?: string;
	mode: AuthenticationFormMode;
	accountId?: string;
	// limits the list of available types, a single type gets preselected
	authTypes?: string[];
	title?: string;
	onSaved?: (data: AuthenticationRecord) => void;
	onClose?: () => void;
};

export interface AuthenticationFieldsStore {
	validator: ModelValidator<object>;
	readonly payload: object;
	load(record: Partial<AuthenticationRecord>): void;
	validate(): boolean;
	destroy(): void;
}

export type SimpleFieldKind = 'text' | 'password' | 'secret' | 'select';

export type SimpleField = {
	name: string;
	label: string;
	// 'secret' is never sent back by the server, only a `has<Name>` flag is
	kind?: SimpleFieldKind;
	// a stored secret can be cleared via `clear<Name>` flag
	clearable?: boolean;
	multiline?: boolean;
	options?: AntSelectOption[];
	defaultValue?: string;
};

export enum OAuthValidationStep {
	Start = 1,
	WaitingForUser = 2,
	Changed = 3,
	Validated = 4,
}

export type OAuthConfigField = 'apiKey' | 'apiSecret' | 'authServer' | 'tokenServer' | 'scope';

export type OAuthConfig = Record<OAuthConfigField, string>;

export type OAuthNotice = {
	type: 'info' | 'error';
	text: string;
	url?: string;
	expires?: number;
};

export type AuthenticationFormProps = {
	store: AuthenticationFormStore;
};

export type AuthenticationTypeFieldsProps = {
	store: AuthenticationFieldsStore;
};

export type AuthenticationFormFooterProps = {
	store: AuthenticationFormStore;
	extra: {
		OkBtn: React.FC;
		CancelBtn: React.FC;
	};
};

export type SimpleFieldsProps = {
	store: SimpleFieldsStore;
};

export type OAuthValidationNoticeProps = {
	store: OAuthValidationStore;
};
